import { formatDate } from '../utils/formatters';

/** Minimal transaction shape used by the Transactions table filters */
export interface TransactionFilterRow {
  id: number;
  date: string;
  amount: number;
  description: string;
  merchant_name: string | null;
  linked_account_id: number;
  manual_account_id: number | null;
  category_id: number | null;
  subcategory_id: number | null;
  is_verified?: boolean;
  categorization_source?: 'manual' | 'rule' | 'system' | null;
  categorized_by_rule_id?: number | null;
  splits?: Array<{ category_id?: number | null; subcategory_id?: number | null }>;
  rule_suggestion?: {
    cluster_key: string;
    cluster_key_type: 'merchant' | 'description_prefix';
    count: number;
    category_id: number;
    subcategory_id: number | null;
    category_name: string;
    subcategory_name: string | null;
  } | null;
}

export interface InlineTransactionFilters {
  dateRange: string;
  description: string;
  accounts: string[];
  categories: string[];
  amountMin: string;
  amountMax: string;
}

export interface FilterTransactionsListOptions {
  typeFilter: string | null;
  statusFilters: string[];
  inlineFilters: InlineTransactionFilters;
  stickyVisibleTransactions: Set<number>;
  /** Local “today” for yearless date filters; defaults to `new Date()` when omitted */
  referenceDateForDateFilter?: Date;
}

/** Calendar day key for comparisons (local) */
function calendarKey(d: Date): number {
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

/** Month token: strip trailing periods (en-CA “Apr.”) and lowercase */
function normalizeMonthLetters(raw: string): string {
  return raw.toLowerCase().replace(/\.+$/g, '').trim();
}

const MONTH_ALIASES: { aliases: string[]; monthIndex: number }[] = [
  { aliases: ['january', 'jan'], monthIndex: 0 },
  { aliases: ['february', 'feb'], monthIndex: 1 },
  { aliases: ['march', 'mar'], monthIndex: 2 },
  { aliases: ['april', 'apr'], monthIndex: 3 },
  { aliases: ['may'], monthIndex: 4 },
  { aliases: ['june', 'jun'], monthIndex: 5 },
  { aliases: ['july', 'jul'], monthIndex: 6 },
  { aliases: ['august', 'aug'], monthIndex: 7 },
  { aliases: ['september', 'sept', 'sep'], monthIndex: 8 },
  { aliases: ['october', 'oct'], monthIndex: 9 },
  { aliases: ['november', 'nov'], monthIndex: 10 },
  { aliases: ['december', 'dec'], monthIndex: 11 },
];

/** Months whose English name/abbreviation matches user prefix (either direction) */
function monthsMatchingPrefix(monthToken: string): number[] {
  const p = normalizeMonthLetters(monthToken);
  if (!p) return [];
  const out = new Set<number>();
  for (const { aliases, monthIndex } of MONTH_ALIASES) {
    for (const a of aliases) {
      if (a.startsWith(p) || p.startsWith(a)) {
        out.add(monthIndex);
        break;
      }
    }
  }
  return [...out];
}

/**
 * Latest calendar occurrence of (month, day) that is on or before `ref` (by calendar date).
 */
function resolveLatestMonthDayOnOrBefore(monthIndex: number, day: number, ref: Date): Date | null {
  const refKey = calendarKey(ref);
  for (let y = ref.getFullYear(); y >= ref.getFullYear() - 200; y--) {
    const d = new Date(y, monthIndex, day);
    if (d.getMonth() !== monthIndex || d.getDate() !== day) continue;
    if (calendarKey(d) <= refKey) return d;
  }
  return null;
}

type ProgressiveResult =
  | { kind: 'noop' }
  | { kind: 'month-only'; months: Set<number> }
  | { kind: 'exact-keys'; keys: Set<number> };

function tryProgressiveDateFilter(trimmed: string, ref: Date): ProgressiveResult {
  const monthDay = trimmed.match(/^([a-zA-Z.]+)\s+(\d{1,2})(?:\s*,?\s*(\d{4}))?\s*$/i);
  if (monthDay) {
    const monthRaw = monthDay[1];
    const dayNum = parseInt(monthDay[2], 10);
    const yearOpt = monthDay[3] ? parseInt(monthDay[3], 10) : null;
    if (dayNum < 1 || dayNum > 31) return { kind: 'noop' };

    const months = monthsMatchingPrefix(monthRaw);
    if (months.length === 0) return { kind: 'noop' };

    const keys = new Set<number>();

    if (yearOpt !== null) {
      for (const m of months) {
        const d: Date = new Date(yearOpt, m, dayNum);
        if (d.getFullYear() !== yearOpt || d.getMonth() !== m || d.getDate() !== dayNum) continue;
        keys.add(calendarKey(d));
      }
    } else {
      for (const m of months) {
        const resolved = resolveLatestMonthDayOnOrBefore(m, dayNum, ref);
        if (resolved) keys.add(calendarKey(resolved));
      }
    }

    if (keys.size === 0) return { kind: 'noop' };
    return { kind: 'exact-keys', keys };
  }

  const monthOnly = trimmed.match(/^([a-zA-Z.]+)\s*$/i);
  if (monthOnly) {
    const months = monthsMatchingPrefix(monthOnly[1]);
    if (months.length === 0) return { kind: 'noop' };
    return { kind: 'month-only', months: new Set(months) };
  }

  return { kind: 'noop' };
}

/** Calendar date for the transaction row, consistent with formatDate() */
function transactionLocalDate(transactionDate: string): Date {
  const isoPrefix = transactionDate.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoPrefix) {
    return new Date(
      parseInt(isoPrefix[1], 10),
      parseInt(isoPrefix[2], 10) - 1,
      parseInt(isoPrefix[3], 10)
    );
  }
  return new Date(transactionDate);
}

/**
 * Date filter:
 * - Exact table label (formatDate) for this row
 * - YYYY-MM-DD
 * - Legacy inclusive range when input contains " - "
 * - Progressive: month prefix (e.g. "Apr" → all April dates), month + day without year
 *   (latest calendar occurrence on or before referenceDate), optional explicit 4-digit year
 * - Fallback: Date.parse-style single day
 */
export function matchesTransactionDateFilter(
  transactionDate: string,
  filterRaw: string,
  referenceDate: Date = new Date()
): boolean {
  const dateRange = filterRaw.trim();
  if (!dateRange) return true;

  const transactionDateObj = transactionLocalDate(transactionDate);

  if (formatDate(transactionDate) === dateRange) return true;

  const ymd = dateRange.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (ymd) {
    const filterDay = new Date(
      parseInt(ymd[1], 10),
      parseInt(ymd[2], 10) - 1,
      parseInt(ymd[3], 10)
    );
    return transactionDateObj.toDateString() === filterDay.toDateString();
  }

  const parseLocalDate = (str: string) => {
    const d = new Date(str + 'T00:00:00');
    return Number.isNaN(d.getTime()) ? new Date(str) : d;
  };

  if (dateRange.includes(' - ')) {
    const [startStr, endStr] = dateRange.split(' - ');
    const startDate = parseLocalDate(startStr.trim());
    const endDate = parseLocalDate(endStr.trim());
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return true;
    if (transactionDateObj < startDate || transactionDateObj > endDate) return false;
    return true;
  }

  const progressive = tryProgressiveDateFilter(dateRange, referenceDate);
  if (progressive.kind === 'month-only') {
    return progressive.months.has(transactionDateObj.getMonth());
  }
  if (progressive.kind === 'exact-keys') {
    return progressive.keys.has(calendarKey(transactionDateObj));
  }

  const filterDate = parseLocalDate(dateRange);
  if (Number.isNaN(filterDate.getTime())) return true;
  return transactionDateObj.toDateString() === filterDate.toDateString();
}

export function filterTransactionsList<T extends TransactionFilterRow>(
  transactions: T[],
  {
    typeFilter,
    statusFilters,
    inlineFilters,
    stickyVisibleTransactions,
    referenceDateForDateFilter,
  }: FilterTransactionsListOptions
): T[] {
  const refDate = referenceDateForDateFilter ?? new Date();

  return transactions.filter(transaction => {
    if (typeFilter === 'income' && transaction.amount <= 0) return false;
    if (typeFilter === 'expense' && transaction.amount >= 0) return false;

    if (statusFilters.length > 0) {
      const hasVerified = statusFilters.includes('verified');
      const hasUnverified = statusFilters.includes('unverified');
      const hasAutoCategorized = statusFilters.includes('auto-categorized');

      let matchesStatus = false;
      if (hasVerified && transaction.is_verified) matchesStatus = true;
      if (hasUnverified && !transaction.is_verified) matchesStatus = true;
      if (
        hasAutoCategorized &&
        !transaction.is_verified &&
        transaction.categorization_source &&
        transaction.categorization_source !== 'manual'
      ) {
        matchesStatus = true;
      }

      if (!matchesStatus) return false;
    }

    if (inlineFilters.description) {
      const searchLower = inlineFilters.description.toLowerCase();
      if (!transaction.description.toLowerCase().includes(searchLower)) return false;
    }

    if (inlineFilters.accounts.length > 0) {
      const effectiveId = transaction.manual_account_id ?? transaction.linked_account_id;
      const accountMatch = inlineFilters.accounts.some(
        accountId => effectiveId === parseInt(accountId, 10)
      );
      if (!accountMatch) return false;
    }

    if (inlineFilters.categories.length > 0) {
      const wasVisible = stickyVisibleTransactions.has(transaction.id);
      let matchesCategory = false;

      for (const categoryFilter of inlineFilters.categories) {
        if (categoryFilter === 'uncategorized') {
          const hasDirectCategory = !!transaction.category_id;
          const hasSplits = transaction.splits && transaction.splits.length > 0;
          if ((!hasDirectCategory && !hasSplits) || wasVisible) {
            matchesCategory = true;
            break;
          }
        } else if (categoryFilter.startsWith('cat-')) {
          const categoryId = parseInt(categoryFilter.replace('cat-', ''), 10);
          const hasDirectMatch = transaction.category_id === categoryId;
          const hasSplitMatch =
            transaction.splits &&
            transaction.splits.some(split => split.category_id === categoryId);
          if (hasDirectMatch || hasSplitMatch || wasVisible) {
            matchesCategory = true;
            break;
          }
        } else if (categoryFilter.startsWith('sub-')) {
          const subcategoryId = parseInt(categoryFilter.replace('sub-', ''), 10);
          const hasDirectMatch = transaction.subcategory_id === subcategoryId;
          const hasSplitMatch =
            transaction.splits &&
            transaction.splits.some(split => split.subcategory_id === subcategoryId);
          if (hasDirectMatch || hasSplitMatch || wasVisible) {
            matchesCategory = true;
            break;
          }
        }
      }

      if (!matchesCategory) return false;
    }

    if (inlineFilters.amountMin !== '') {
      const min = parseFloat(inlineFilters.amountMin);
      if (!Number.isNaN(min) && Math.abs(transaction.amount) < min) return false;
    }
    if (inlineFilters.amountMax !== '') {
      const max = parseFloat(inlineFilters.amountMax);
      if (!Number.isNaN(max) && Math.abs(transaction.amount) > max) return false;
    }

    if (!matchesTransactionDateFilter(transaction.date, inlineFilters.dateRange, refDate)) {
      return false;
    }

    return true;
  });
}
